import { useState } from "react";
import { validateGuestName, GUEST_NAME_MAX_LENGTH } from "../../../lib/guest-voting";

// Pre-vote name-entry card for an anonymous guest on a link-shared scheduler.
// Shown before the voting grid when the scheduler is not fully anonymous and
// the guest has no existing vote. Validates locally (trim, non-empty, <=40),
// then calls onSubmit with the cleaned name. Presentational + local state only
// — the SchedulerPage owns the vote payload / persistence.
export default function GuestNameCard({ onSubmit }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState(null);

  const result = validateGuestName(value);
  const canSubmit = result.valid;

  function handleSubmit(e) {
    e.preventDefault();
    const r = validateGuestName(value);
    if (!r.valid) {
      setError(r.error);
      return;
    }
    setError(null);
    onSubmit(r.value);
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mx-auto flex max-w-md flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800"
      aria-labelledby="guest-name-title"
    >
      <h2 id="guest-name-title" className="text-lg font-semibold text-slate-800 dark:text-slate-100">
        What should we call you?
      </h2>
      <p className="text-sm text-slate-600 dark:text-slate-300">
        Enter a name so the organizer can see your vote.
      </p>
      <label htmlFor="guest-name-input" className="sr-only">
        Your name
      </label>
      <input
        id="guest-name-input"
        type="text"
        autoComplete="nickname"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setError(null);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleSubmit(e);
          }
        }}
        maxLength={GUEST_NAME_MAX_LENGTH}
        placeholder="Your name"
        aria-invalid={Boolean(error)}
        aria-describedby={error ? "guest-name-error" : undefined}
        className="min-h-[44px] w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-brand-primary focus-visible:ring-2 focus-visible:ring-brand-primary dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
      />
      {error && (
        <p id="guest-name-error" role="alert" className="text-sm font-medium text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={!canSubmit}
        className="min-h-[44px] w-full rounded-full bg-brand-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Continue
      </button>
    </form>
  );
}
